import { User, IUser, ITenant } from '@koomzo/commonmodel';
import { State, Selector, Action, StateContext, Store } from '@ngxs/store';
import { Logout, EmailLogin, LogIn, RestoreSession, UpdatePushToken, UpdateUserProfile, UpdatePushTokenAndLocation } from './auth.actions';
import { AUTH_GATEWAY_PORT, AuthGatewayPort } from '@shared/services/backend';
import { from, of, throwError, tap, catchError, switchMap, firstValueFrom, take } from 'rxjs';
import { TenantsDbService, UsersDbService } from '@shared/services';
import { SetCurrentTenant } from '@shared/store/app.actions';
import { LoadTenantCapabilities } from '@shared/store/capability.actions';
import { Inject, Injectable, inject } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { kzMark } from '@shared/util/kz-perf';
import { sessionRoleFor } from '@common/auth/models/session-role';
import { hintMatches, readSessionHint, writeSessionHint } from './session-hint';

export class AuthStateModel {
  hasLoaded: boolean;
  isAuthenticated: boolean;
  user?: User;
}

@State<AuthStateModel>({
  name: 'authState',
  defaults: {
    hasLoaded: false,
    isAuthenticated: false,
    user: null
  }
})
@Injectable()
export class AuthState {
  @Selector()
  static token(state: AuthStateModel) {
    return state.user.idToken;
  }

  @Selector()
  static uId(state: AuthStateModel) {
    return state.user.id;
  }

  @Selector()
  static tenantCode(state: AuthStateModel) {
    return state.user.tenantSiteId;
  }

  @Selector()
  static tenantSiteCode(state: AuthStateModel) {
    return state.user.tenantId;
  }

  /** The identity provider, through its port (kz2 F1, ruling Q4): never `AuthService` or Firebase. */
  constructor(
    @Inject(AUTH_GATEWAY_PORT) private authGateway: AuthGatewayPort,
    private usersDbService: UsersDbService,
    private _store: Store,
    private tenantsDbService: TenantsDbService
  ) {}

  @Action(LogIn)
  logIn({ setState, patchState }: StateContext<AuthStateModel>, { payload }: any) {
    return from(this.authGateway.emailLogin(payload.username, payload.password)).pipe(
      tap((result: User) => {
        //result.initRoleAttributes();
        writeSessionHint(result);
        setState({
          hasLoaded: true,
          isAuthenticated: true,
          user: result
        });
      }),
      catchError((err, caught) => {
        setState({
          hasLoaded: false,
          isAuthenticated: false,
          user: null
        });
        return throwError(err);
      })
    );
  }

  /**
   * Restore the session at start-up (an APP_INITIALIZER: the guards read it synchronously). kz2 M12
   * (R11 B, C): the tenant this device signed in to last (`session-hint.ts`) is read IN PARALLEL with
   * the profile, and used only when the profile names the same uid and tenant — else the tenant is
   * read after the profile, as before. The tenant's capability document starts following as soon as
   * the tenant is known (not awaited; seeded from this device's cached copy).
   */
  @Action(RestoreSession)
  restoreSession(ctx: StateContext<AuthStateModel>) {
    const hint = readSessionHint();
    const early = hint ? this.readTenant(hint.tenantId) : null;
    return from(this.authGateway.restoreSession()).pipe(
      switchMap((user: User) => {
        kzMark('session:user', user ? 'yes' : 'none');
        if (!user) {
          // If no user is returned, leave state as-is (unauthenticated).
          writeSessionHint(null);
          return of(null);
        }
        ctx.setState({
          hasLoaded: true,
          isAuthenticated: true,
          user
        });
        writeSessionHint(user);
        // Rehydrate the current tenant so tenant-scoped screens (e.g. the forms
        // app manager) work after a reload / deep-link. Login dispatches this
        // too; restore previously skipped it, leaving `currentTenant` null.
        // Chained here so the APP_INITIALIZER blocks bootstrap (and the guards)
        // until the tenant is loaded.
        if (user.tenantId) {
          // The shell's populateMenu dispatches this again: the load is idempotent per tenant.
          ctx.dispatch(new LoadTenantCapabilities({ tenantId: user.tenantId, role: sessionRoleFor(user) ?? 'staff' }));
          if (early && hintMatches(hint, user)) {
            return from(early).pipe(
              switchMap((tenant) => {
                kzMark('session:tenant-hint', tenant ? 'hit' : 'miss');
                return ctx.dispatch(new SetCurrentTenant({ id: user.tenantId, tenant }));
              }),
            );
          }
          kzMark('session:tenant-hint', hint ? 'stale' : 'none');
          return ctx.dispatch(new SetCurrentTenant({ id: user.tenantId }));
        }
        return of(null);
      }),
      catchError(() => {
        // Never throw during bootstrap: leave state unauthenticated so the
        // app still loads the login page.
        return of(null);
      })
    );
  }

  /** The hinted tenant, read once; `null` on any failure (the restore then reads it the usual way). */
  private readTenant(tenantId: string): Promise<ITenant | null> {
    try {
      return firstValueFrom(this.tenantsDbService.getById(tenantId).pipe(take(1)), { defaultValue: null })
        .then((t) => (t && (t as ITenant).id === tenantId ? (t as ITenant) : null))
        .catch(() => null);
    } catch {
      return Promise.resolve(null);
    }
  }

  @Action(EmailLogin)
  emailLogin(context: StateContext<AuthStateModel>, { payload }: EmailLogin) {
    return context.patchState({ user: payload });
  }

  @Action(Logout)
  logOut({ setState, getState }: StateContext<AuthStateModel>) {
    const state = getState();
    const pState = { ...state, user: null, hasLoaded: false };
    writeSessionHint(null);
    void this.dismissToasts();
    return from(this.authGateway.logout()).pipe(tap(() => setState(pState)));
  }

  private readonly toasts = inject(ToastController, { optional: true });

  /**
   * A toast of the session that ends goes with it (kz2 M12 RB-4): "Profile saved" used to wait on the login
   * page, and on the next person's home. Bounded, and never in the way of the sign-out itself.
   */
  private async dismissToasts(): Promise<void> {
    try {
      for (let i = 0; i < 10; i++) {
        const top = await this.toasts?.getTop();
        if (!top) return;
        await top.dismiss();
      }
    } catch {
      /* nothing to dismiss */
    }
  }

  // @Action(Logout)
  // logOut({ getState, patchState }: StateContext<AuthStateModel>) {
  //   const state = getState();
  //   const pState = { ...state, user: null, hasLoaded: false };
  //   return from(this.authGateway.logout()).pipe(tap(() => patchState({ user: null, hasLoaded: false })));
  // }

  @Action(UpdatePushToken)
  UpdatePushToken(context: StateContext<AuthStateModel>, { payload }: UpdatePushToken) {
    const state = context.getState();
    const pUser = new User({ ...state.user, pushToken: payload.pushToken });
    return from(this.usersDbService.addOrUpdate(pUser)).pipe(
      tap((data: User) => {
        console.log('user pushtoken:', data.pushToken);
        context.patchState({ user: data });
      })
    );
  }

  @Action(UpdatePushTokenAndLocation)
  UpdatePushTokenAndLocation(context: StateContext<AuthStateModel>, { payload }: UpdatePushTokenAndLocation) {
    const state = context.getState();
    const pUser = new User({ ...state.user, pushToken: payload.pushToken, location:payload.location });
    return from(this.usersDbService.addOrUpdate(pUser)).pipe(
      tap((data: User) => {
        console.log('user pushtoken:', data.pushToken);
        context.patchState({ user: data });
      })
    );
  }

  @Action(UpdateUserProfile)
  updateUserProfile(ctx: StateContext<AuthStateModel>, { payload }) {
    return from(this.usersDbService.addOrUpdate(payload)).pipe(
      tap((result: IUser) => {
        const state = ctx.getState();
        ctx.patchState({ ...state, user: result });
      }),
      catchError((err, caught) => {
        console.log('Error updating Teanant');
        return throwError(err);
      })
    );
  }
}
